import React from "react";
import { motion } from "framer-motion";
import { ArrowDownIcon, CalendarIcon, HomeIcon, LeafIcon, MailIcon, PieChartIcon, UsersIcon, BoxIcon } from "lucide-react";
import { useLanguage } from "../LanguageContext";
import { BROSSARD_IMAGES, proofCopy, proofMetrics, Bi } from "../../data/brossardCase";
import { BrossardLaptop, BrossardPhone } from "./BrossardDevices";
const EASE = [0.23, 1, 0.32, 1] as const;
const METRIC_ICONS: Record<(typeof proofMetrics)[number]['key'], BoxIcon> = {
  identities: UsersIcon,
  coverage: PieChartIcon,
  since: CalendarIcon
};
function FloatIcon({
  Icon,
  className
}: {
  Icon: BoxIcon;
  className: string;
}) {
  return <span className={`absolute w-11 h-11 rounded-xl bg-white border border-[#ECEBF7] shadow-[0_10px_24px_-14px_rgba(79,70,229,0.45)] flex items-center justify-center ${className}`} aria-hidden="true" data-id="element-1306" data-mp="1a3153i7c321b">
      <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} data-id="element-1307" data-mp="hecbs5cis104" />
    </span>;
}
export function BrossardProof() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  const scrollToTransform = () => {
    document.getElementById('brossard-transformation')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <section className="relative overflow-hidden bg-white px-6 pt-12 lg:pt-16 pb-14 lg:pb-16" aria-labelledby="brossard-proof-title" data-id="element-1308" data-mp="1vyj27ztvibml">
      <div className="pointer-events-none absolute -top-32 -left-32 w-[620px] h-[420px] rounded-full bg-[#F1F0FD] opacity-70 blur-[130px]" aria-hidden="true" data-id="element-1309" data-mp="1mze2wrahme9d" />
      <div className="relative max-w-[1320px] mx-auto grid grid-cols-1 xl:grid-cols-[1fr_1.05fr] gap-12 xl:gap-8 items-center" data-id="element-1310" data-mp="1hz9kuwtxlbh1">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-1311" data-mp="18zw9fym8ycto">
          <p className="font-sans text-[12.5px] font-semibold tracking-[0.24em] uppercase text-[#121A3A]" data-id="element-1312" data-mp="37d68a63fjtxr">
            {pick(proofCopy.eyebrow)} · <span className="text-[#4F46E5]" data-id="element-1313" data-mp="10r06n3k8rn5v">{proofCopy.eyebrowCity}</span>
          </p>
          <h1 id="brossard-proof-title" className="mt-4 font-serif text-[2.3rem] sm:text-[2.9rem] lg:text-[3.2rem] leading-[1.02] tracking-tight" data-id="element-1314" data-mp="15iibi2pdy7w4">
            {proofCopy.headline.map(l => <span key={l.text.fr} className={`block ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-1315" data-mp="vs5gfgjy2wbq">{pick(l.text)}</span>)}
          </h1>
          <p className="mt-5 max-w-[560px] font-sans text-[15.5px] md:text-[16.5px] text-[#4A5268] leading-relaxed" data-id="element-1316" data-mp="1jit4bba99wjx">
            {pick(proofCopy.bodyStart)}
            <strong className="font-semibold text-[#121A3A]" data-id="element-1317" data-mp="1ah1w9zs8tdmw">{pick(proofCopy.bodyStrong)}</strong>
          </p>

          <dl className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-[640px]" data-id="element-1318" data-mp="1ffwfh2u4h47j">
            {proofMetrics.map(m => {
            const Icon = METRIC_ICONS[m.key];
            return <div key={m.key} className="rounded-xl border border-[#ECEBF5] bg-white px-4 py-3.5 flex items-start gap-3" data-id="element-1319" data-mp="3v9na84gj5u5m">
                  <span className="w-9 h-9 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" data-id="element-1320" data-mp="3rotuuz2iw43h">
                    <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-1321" data-mp="3nojbmkppzh6o" />
                  </span>
                  <div className="min-w-0" data-id="element-1322" data-mp="5hmvz9m0s0sz">
                    <dt className={`font-sans font-semibold text-[#121A3A] leading-none whitespace-nowrap ${m.key === 'since' ? 'font-serif font-normal text-[1.35rem]' : 'text-[1.45rem]'}`} data-id="element-1323" data-mp="j24pzoqwrgo">
                      {pick(m.value)}
                    </dt>
                    <dd className="mt-1.5 font-sans text-[12.5px] text-[#4A5268] leading-snug" data-id="element-1324" data-mp="341dcop8z1pq9">{pick(m.label)}</dd>
                    {m.note && <dd className="mt-0.5 font-sans text-[10.5px] text-[#8A8FA6]" data-id="element-1325" data-mp="3iqrjpqp2rv2h">{pick(m.note)}</dd>}
                  </div>
                </div>;
          })}
          </dl>

          <button type="button" onClick={scrollToTransform} className="mt-7 inline-flex items-center gap-3 rounded-lg bg-[#4F46E5] px-6 py-3 font-sans text-[14.5px] font-medium text-white hover:bg-[#4338CA] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:ring-offset-2" data-id="element-1326" data-mp="2q6t2mxmvkcku">
            {pick(proofCopy.cta)}
            <ArrowDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-1327" data-mp="1xic5qt59zptl" />
          </button>
        </motion.div>

        {/* Product composition — desktop */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.08,
        ease: EASE
      }} className="relative hidden md:block h-[440px]" data-id="element-1328" data-mp="22bf0h4lsh5bd">
          <div className="absolute inset-y-0 right-[-8%] left-[18%] overflow-hidden rounded-[28px]" aria-hidden="true" data-id="element-1329" data-mp="2h7wds1djgu0k">
            <img src={BROSSARD_IMAGES.panorama} alt="" className="w-full h-full object-cover opacity-80" data-id="element-1330" data-mp="2l7dkey4pbx7b" />
            <div className="absolute inset-0 bg-[linear-gradient(90deg,#fff_0%,rgba(255,255,255,0.75)_30%,rgba(255,255,255,0.2)_70%,rgba(255,255,255,0)_100%)]" data-id="element-1331" data-mp="mun160pp4se3" />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-[linear-gradient(0deg,#fff_0%,rgba(255,255,255,0)_100%)]" data-id="element-1332" data-mp="jbo6q9mymug1" />
          </div>
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 600 440" preserveAspectRatio="none" aria-hidden="true" data-id="element-1333" data-mp="ehth6pkobl91">
            <path d="M70 70 C 30 110, 40 150, 70 180" fill="none" stroke="#C9C5F6" strokeWidth="1" data-id="element-1334" data-mp="5jhtowf3t3kt" />
            <path d="M92 70 C 150 70, 170 80, 200 100" fill="none" stroke="#C9C5F6" strokeWidth="1" data-id="element-1335" data-mp="1298l2ms3mame" />
            <path d="M300 55 C 320 70, 330 90, 340 110" fill="none" stroke="#C9C5F6" strokeWidth="1" data-id="element-1336" data-mp="xann6u0bk314" />
            <path d="M560 200 C 540 220, 530 240, 520 260" fill="none" stroke="#C9C5F6" strokeWidth="1" data-id="element-1337" data-mp="t8aqsw7gphh0" />
          </svg>
          <FloatIcon Icon={MailIcon} className="left-[8%] top-[10%]" data-id="element-1338" data-mp="o8o47w5quovj" />
          <FloatIcon Icon={LeafIcon} className="left-[6%] top-[38%]" data-id="element-1339" data-mp="3dyrsnvnp5sod" />
          <FloatIcon Icon={CalendarIcon} className="left-[47%] top-[4%]" data-id="element-1340" data-mp="3dwyk2atiwydq" />
          <FloatIcon Icon={HomeIcon} className="right-[2%] top-[40%]" data-id="element-1341" data-mp="38ydhw5upo4mn" />
          <div className="absolute left-[34%] top-[20%]" data-id="element-1342" data-mp="3agu6606ok13t">
            <BrossardLaptop pick={pick} lang={lang} data-id="element-1343" data-mp="3afap47d79m0z" />
          </div>
          <div className="absolute left-[16%] top-[2%]" data-id="element-1344" data-mp="3ffosvd7rq9t7">
            <BrossardPhone pick={pick} data-id="element-1345" data-mp="31gytts1e2027" />
          </div>
        </motion.div>

        {/* Product composition — mobile */}
        <div className="md:hidden flex justify-center" data-id="element-1346" data-mp="35gpqudhidcf6">
          <BrossardPhone pick={pick} data-id="element-1347" data-mp="2rqm7pvdxdyty" />
        </div>
      </div>
    </section>;
}